import Duration from "app/singing/vocal-driller/constants/Duration";
import { rangeToMidi } from "app/singing/vocal-driller/constants/ranges";
import {
  getMaxScaleRootMidiForDegreesWithinRange,
  getScale,
  Scale,
} from "app/singing/vocal-driller/constants/scales";
import shortHandToLoopSteps from "app/singing/vocal-driller/shorthand/shorthandToLoopSteps";
import { LoopStep } from "../loopSteps";
import { MutatableInternalState } from "../useMutatableInternalState";
import { PlayerExternalState } from "./useExternalState";
import { getAllRegisterRangesFromRange } from "../../constants/vocalRegister";

type Params = Pick<
  PlayerExternalState,
  "shorthands" | "range" | "scaleType" | "vocalRegister"
>;
type Return = Pick<
  MutatableInternalState,
  "steps" | "scales" | "scaleIndex" | "stepIndex"
>;

export default function convertMelodyRangeScaleToInternalState(
  externalState: Params
): Return {
  if (!externalState.range) {
    return {
      steps: [],
      scales: [],
      scaleIndex: 0,
      stepIndex: 0,
    };
  }

  if (!externalState.shorthands) {
    return {
      steps: [],
      scales: [],
      scaleIndex: 0,
      stepIndex: 0,
    };
  }

  const range = getAllRegisterRangesFromRange(externalState.range)[
    externalState.vocalRegister
  ];

  const [lowRangeMidiNote] = rangeToMidi(range);
  const steps = shortHandToLoopSteps({
    melodyShorthand: externalState.shorthands.melody,
  }).melodySteps;

  const maxDegree = (() => {
    const degree = Math.max(
      ...steps.map((step) => {
        if (step.type === "rest") {
          return 0;
        }

        const degrees = step.melody?.degreesToPlay || [];

        return Math.max(...degrees);
      })
    );

    if (isFinite(degree)) {
      return degree;
    }

    return 1;
  })();

  let maxScaleRoot = getMaxScaleRootMidiForDegreesWithinRange({
    scaleType: externalState.scaleType,
    maxDegree,
    range,
  });

  // If maxScaleRoot is not at least 1 higher then the lowRangeMidiNote, then make it it one higher
  if (maxScaleRoot <= lowRangeMidiNote) {
    maxScaleRoot = lowRangeMidiNote + 1;
  }

  const scales: Scale[] = [];

  for (let i = lowRangeMidiNote; i < maxScaleRoot; i++) {
    scales.push(getScale(i, externalState.scaleType));
  }

  return {
    steps: addPreChordsToSteps(steps),
    scales,
    scaleIndex: 0,
    stepIndex: 0,
  };
}

function addPreChordsToSteps(steps: LoopStep[]): LoopStep[] {
  const prechords: LoopStep[] = [
    {
      type: "degree",
      metronome: true,
      lastStep: false,
      melody: undefined,
      chord: {
        duration: Duration.halfNote,
        scaleDegree: 1,
      },
    },
    { type: "rest", lastStep: false, metronome: true },
  ];

  return steps.length > 0 ? [...prechords, ...steps] : steps;
}
